import { useCallback, useEffect, useMemo, useState } from "react";
import { ActionBar, Button, Input, LoadingState, Select } from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import type { Membership, Season } from "../../../lib/queries/memberships";
import { useRuleSetDocument } from "../../../lib/queries/rules";
import { ageReferenceLabel } from "../../../lib/entryChecks";
import { AgeBadge } from "../../../components/AgeBadge";
import { PersonChip } from "./PersonChip";
import {
  REVIZE_GROUPS_FROM,
  REVIZE_SEARCH_FROM,
  candidateMatches,
  groupCandidates,
  pickAgeTable,
  seasonReferenceDate,
  type RevizeGroup,
} from "./revizeGroups";
import styles from "./TransferRevize.module.css";
import type { ID } from "../../../lib/id";

/**
 * The transfer revize: every candidate from the source season opens
 * visibly SELECTED, and the leader's work is unselecting the few who do
 * not continue. Nothing is written until „Převést N členů" stages the
 * whole selection into the draft at once; the footer therefore verifies
 * the short list (who is NOT going), never the long one.
 *
 * It is the page's first step while the soupiska is empty: open, it is
 * the whole page and owns the sticky bar (the draft's bar replaces it on
 * the sheet). Once committed or skipped it collapses to a one-line card
 * that reopens it, so a registered roster shows that card instead of a
 * wall of chips and the transfer stays an explicit act. With a season
 * picked and nobody left to transfer it says so rather than disappearing.
 *
 * Searching never filters the wall: hiding chips made the commit look
 * smaller than it is („am I transferring just the one I typed?"). It
 * highlights instead: matches stay crisp, the rest dim, and a counter
 * says how many matched out of how many. Enter toggles the first match.
 */

const czechVerb = (n: number, one: string, more: string) => (n >= 2 && n <= 4 ? more : one);

export interface TransferRevizeProps {
  /** Source-season members not on the roster, not the leader, not staged. */
  candidates: Membership[];
  /** Source-season members registered with another club this season. */
  departed?: Membership[];
  candidatesLoading: boolean;
  /** Seasons offered as the transfer source (non-active ones). */
  selectableSeasons: Season[];
  sourceSeasonId: ID | null;
  /** The season being registered into; its year anchors the age ladder. */
  targetSeason: Season;
  onSetSeason: (id: ID | null) => void;
  onCommit: (selected: Membership[]) => void;
  /**
   * The page's first step („transfer first, then the sheet"). Open, the
   * panel is the whole page and carries its own sticky `ActionBar`, so the
   * transfer's buttons sit where the draft's will later. Collapsed, it is a
   * one-line card that reopens it. Kept mounted in both states so the
   * unselected set survives the round trip.
   */
  open: boolean;
  /** People already staged from a transfer, for the collapsed line. */
  stagedCount: number;
  onOpen: () => void;
  /** Leave the transfer step without committing anything. */
  onLeave: () => void;
  /** What leaving is called: skipping the step, or returning to the sheet. */
  leaveLabel: string;
}

export function TransferRevize({
  candidates,
  departed = [],
  candidatesLoading,
  selectableSeasons,
  sourceSeasonId,
  targetSeason,
  onSetSeason,
  onCommit,
  open,
  stagedCount,
  onOpen,
  onLeave,
  leaveLabel,
}: TransferRevizeProps) {
  // Person ids the leader unselected. Kept across refetches and commits;
  // a commit removes the selected from `candidates`, so what remains is
  // exactly this set, rendered as the shrunken residue. Only switching the
  // source season resets it: a different roster is a different decision.
  const [unselected, setUnselected] = useState<ReadonlySet<ID>>(new Set());
  const [query, setQuery] = useState("");
  useEffect(() => {
    setUnselected(new Set());
    setQuery("");
  }, [sourceSeasonId]);

  // The ladder comes from the rule set the federation DESIGNATED for the
  // target season, and from nowhere else.
  //
  // It used to be picked: the active set labelled with this season, else
  // the newest active one of any season. That fall-through is why the
  // panel could group next season's registration by last season's bands
  // and say nothing about it — and it fired more often than not, because
  // registration opens months before a season's rules go active. A season
  // with no default now groups by ročník instead of borrowing.
  const groupingSetId = targetSeason.default_rule_set_id ?? undefined;
  const { data: doc } = useRuleSetDocument(groupingSetId);

  const referenceDate = seasonReferenceDate(targetSeason.code);
  const ageTable = useMemo(() => (doc ? pickAgeTable(doc) : null), [doc]);

  // Only a FIXED-DATE ladder can place somebody for a whole season.
  //
  // `ageOn` reads the reference date differently per mode: with
  // `fixed_date` it takes only the YEAR and applies the ladder's own day
  // (ČNSMS: 31. 12.), so the mid-season date this panel passes never reaches
  // the result and the band is exact. With `at_competition` the date IS
  // the answer — and a registration has no competition, so the band would
  // be computed against 30 June, a day nothing happens on. That is not an
  // approximation, it is a different question: in that mode a person's
  // category genuinely differs from event to event, so no season-wide
  // grouping exists to show. Ročník does, and it is a fact.
  const fixedLadder = ageTable?.age_mode === "fixed_date" ? ageTable : null;

  const groups = useMemo(
    () => groupCandidates(candidates, fixedLadder, referenceDate),
    [candidates, fixedLadder, referenceDate],
  );
  const grouped = candidates.length >= REVIZE_GROUPS_FROM && groups.length > 1;
  const searchable = candidates.length >= REVIZE_SEARCH_FROM;

  // null = no active query; chips render undimmed.
  const matched = useMemo(() => {
    if (!query.trim()) return null;
    return new Set(candidates.filter((m) => candidateMatches(m, query)).map((m) => m.person.id));
  }, [candidates, query]);

  const isSelected = (m: Membership) => !unselected.has(m.person.id);
  const selectedMembers = candidates.filter(isSelected);
  const unselectedMembers = candidates.filter((m) => !isSelected(m));

  // Stable: PersonChip is memoized, and an inline closure would re-render
  // all fifty chips on every keystroke in the search box.
  const toggle = useCallback((m: Membership) => {
    setUnselected((prev) => {
      const next = new Set(prev);
      if (next.has(m.person.id)) next.delete(m.person.id);
      else next.add(m.person.id);
      return next;
    });
  }, []);
  const toggleGroup = (g: RevizeGroup) => {
    const anySelected = g.members.some(isSelected);
    setUnselected((prev) => {
      const next = new Set(prev);
      for (const m of g.members) {
        if (anySelected) next.add(m.person.id);
        else next.delete(m.person.id);
      }
      return next;
    });
  };
  // Panel-wide mirror of the group toggle: one click empties the whole
  // selection (or brings it back), for the leader who transfers only a
  // handful and would otherwise unselect forty chips.
  const toggleAll = () => {
    setUnselected(
      selectedMembers.length > 0
        ? new Set(candidates.map((m) => m.person.id))
        : new Set(),
    );
  };
  const toggleFirstMatch = () => {
    if (!matched) return;
    for (const g of groups) {
      const hit = g.members.find((m) => matched.has(m.person.id));
      if (hit) {
        toggle(hit);
        return;
      }
    }
  };

  if (selectableSeasons.length === 0) return null;

  const n = selectedMembers.length;
  const hasCandidates = sourceSeasonId != null && candidates.length > 0;
  const sourceCode = selectableSeasons.find((s) => s.id === sourceSeasonId)?.code;

  if (!open) {
    return (
      <section className={styles.panel} aria-label="Převod z minulé sezóny">
        <div className={styles.collapsed}>
          <span className={styles.title}>
            {sourceCode ? `Převod ze sezóny ${sourceCode}` : "Převod ze sezóny"}
          </span>
          <span className={styles.collapsedNote}>
            {stagedCount > 0
              ? `Připraveno k převodu: ${stagedCount} ${pluralizeCzech(stagedCount, ["člen", "členové", "členů"])}. Na soupisku se zapíšou odesláním registrace.`
              : "Členy z minulé sezóny můžete převést hromadně, bez vypisování."}
          </span>
          <Button size="sm" onClick={onOpen}>
            {stagedCount > 0 ? "Převést další členy" : "Převést členy"}
          </Button>
        </div>
      </section>
    );
  }

  const chipRow = (members: Membership[]) => (
    <div className={styles.chips}>
      {members.map((m) => (
        <PersonChip
          key={m.person.id}
          membership={m}
          selected={isSelected(m)}
          dimmed={matched != null && !matched.has(m.person.id)}
          onToggle={toggle}
        />
      ))}
    </div>
  );

  let body;
  if (sourceSeasonId == null) {
    body = (
      <p className={styles.prompt}>Vyberte sezónu, ze které chcete převést členy na soupisku.</p>
    );
  } else if (candidatesLoading && candidates.length === 0) {
    body = <LoadingState label="Načítám soupisku…" />;
  } else if (candidates.length === 0) {
    body = (
      <p className={styles.prompt}>
        {departed.length > 0
          ? "Z vybrané sezóny už nikoho dalšího převést nelze."
          : "Z vybrané sezóny už jsou všichni na soupisce."}
      </p>
    );
  } else {
    body = (
      <>
        {matched && (
          <p className={styles.filterNote}>
            {matched.size === 0
              ? "Hledání nikoho nenašlo."
              : `Shody hledání: ${matched.size} z ${candidates.length}. Označení ostatních se nemění.`}
          </p>
        )}
        {grouped ? (
          groups.map((g) => {
            const sel = g.members.filter(isSelected).length;
            return (
              <div className={styles.group} key={g.key}>
                <div className={styles.groupHead}>
                  <span className={styles.groupLabel}>
                    <AgeBadge name={g.label} color={g.color} />
                  </span>
                  <span className={styles.groupCount}>
                    {sel} / {g.members.length}
                  </span>
                  <Button weight="ghost" size="sm" onClick={() => toggleGroup(g)}>
                    {sel > 0 ? "Odznačit skupinu" : "Označit skupinu"}
                  </Button>
                </div>
                {chipRow(g.members)}
              </div>
            );
          })
        ) : (
          <div className={styles.group}>{chipRow(candidates)}</div>
        )}
      </>
    );
  }

  // Verify the short list (who is NOT going), commit the long one. In the
  // sticky bar, so the commit is on screen however long the wall of chips.
  const barStatus = !hasCandidates ? null : (
    <span className={styles.sum}>
      {n > 0 && (
        <>
          {czechVerb(n, "Převede se", "Převedou se")}{" "}
          <strong>
            {n} {pluralizeCzech(n, ["člen", "členové", "členů"])}
          </strong>
        </>
      )}
      {n > 0 && unselectedMembers.length > 0 && " · "}
      {unselectedMembers.length > 0 && (
        <>
          {czechVerb(unselectedMembers.length, "nepřevede se", "nepřevedou se")}{" "}
          {unselectedMembers.length}:{" "}
          {unselectedMembers
            .slice(0, 6)
            .map((m) => `${m.person.first_name} ${m.person.last_name}`)
            .join(", ")}
          {unselectedMembers.length > 6 &&
            ` a ${unselectedMembers.length - 6} ${pluralizeCzech(
              unselectedMembers.length - 6,
              ["další", "další", "dalších"],
            )}`}
        </>
      )}
    </span>
  );

  return (
    <>
      <section className={styles.panel} aria-label="Převod z minulé sezóny">
        <div className={styles.head}>
          <span className={styles.title}>Převod ze sezóny</span>
          <Select
            aria-label="Zdrojová sezóna"
            className={styles.seasonSelect}
            value={sourceSeasonId != null ? String(sourceSeasonId) : ""}
            onChange={(e) => onSetSeason(e.target.value ? e.target.value : null)}
          >
            <option value="">Vyberte sezónu</option>
            {selectableSeasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code}
              </option>
            ))}
          </Select>
          {hasCandidates && searchable && (
            <div className={styles.search}>
              <Input
                aria-label="Hledat kandidáta"
                placeholder="Začněte psát jméno…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Enter toggles the first highlighted match: at fifty
                  // people the keyboard is the fastest pointer there is.
                  if (e.key === "Enter") toggleFirstMatch();
                }}
              />
            </div>
          )}
          {hasCandidates && (
            <span className={styles.sub}>
              Všichni jsou označení k převodu. Odznačte ty, kdo nepokračují; převedení se
              registrují jako <strong>členové</strong> nové sezóny.
            </span>
          )}
          {/* What the headings are, said once, in the caption where the rest
              of the panel's prose lives. Naming the SEASON, not the rule set:
              which document it is is internal, which season's rules apply is
              not. The date is stated because with a fixed-date ladder it is
              exact — and left out otherwise, because there would be nothing
              true to put there. */}
          {grouped && (
            <span className={styles.sub}>
              {fixedLadder
                ? `Věkové kategorie dle pravidel sezóny ${targetSeason.code}, věk ${ageReferenceLabel(fixedLadder, referenceDate)}.`
                : ageTable
                  ? `Pravidla sezóny ${targetSeason.code} počítají věk ke dni soutěže, takže skupiny jsou podle ročníku.`
                  : `Pravidla pro sezónu ${targetSeason.code} zatím nejsou určená, takže skupiny jsou podle ročníku.`}
            </span>
          )}
        </div>
        {body}
        {sourceSeasonId != null && departed.length > 0 && (
          <p className={styles.filterNote}>
            Přestoupili do jiného týmu, nepřevádějí se:{" "}
            {departed
              .map((m) => `${m.person.first_name} ${m.person.last_name} (${m.registered_elsewhere})`)
              .join(", ")}
            .
          </p>
        )}
      </section>
      {/* No live region: the status names up to six people and changes on
          every chip toggle, so announcing it would read the list per click. */}
      <ActionBar aria-label="Převod členů" announce={false} startLayout="row" start={barStatus}>
        {hasCandidates && (
          <Button onClick={toggleAll}>{n > 0 ? "Odznačit vše" : "Označit vše"}</Button>
        )}
        <Button variant={n > 0 ? "neutral" : "primary"} onClick={onLeave}>
          {leaveLabel}
        </Button>
        {n > 0 && (
          <Button variant="primary" onClick={() => onCommit(selectedMembers)}>
            Převést {n} {pluralizeCzech(n, ["člena", "členy", "členů"])}
          </Button>
        )}
      </ActionBar>
    </>
  );
}
